import FacetTypes from '@/types/search/enums/facetTypes';

import { urlSafeTitle } from '../urls';

/**
 * Maps URL dimension segment → search facet id. Extend via data/config when new dimensions ship.
 */
export const BROWSE_DIMENSION_PATH_TO_FACET_TYPE: Readonly<Record<string, string>> = {
    creator: FacetTypes.CREATOR_ID,
    material: FacetTypes.MATERIALS_AND_TECHNIQUES_ID,
    origin: FacetTypes.ORIGIN_ID,
    style: FacetTypes.STYLE_PERIOD_ID,
    subject: FacetTypes.ART_SUBJECT_ID,
};

export const SEARCH_SEMANTIC_MULTI_VALUE_DELIMITER = '~';

const BROWSE_FACET_TYPE_TO_DIMENSION_PATH_KEY: Readonly<Record<string, string>> = Object.freeze(
    Object.fromEntries(Object.entries(BROWSE_DIMENSION_PATH_TO_FACET_TYPE).map(([key, facetType]) => [facetType, key]))
);

/**
 * Browse dimensions backed by taxonomy category ids. Search-party expects these ids on `searchQuery.categories`
 * as well as under `options` (see category browse loaders). Keyword search with semantic params only populated
 * `options` before, which breaks F2 (origin) filtering.
 */
export const BROWSE_SEMANTIC_FACET_IDS_MERGED_INTO_CATEGORIES: ReadonlySet<string> = new Set([
    FacetTypes.CREATOR_ID,
    FacetTypes.STYLE_PERIOD_ID,
    FacetTypes.MATERIALS_AND_TECHNIQUES_ID,
    FacetTypes.ORIGIN_ID,
]);

export type BrowsePathBase = '/c' | '/price-guide' | '/similar-items';

export type BrowseDimensionPair = {
    dimensionKey: string;
    valueSlug: string;
};

export type BrowseCategoryRawFacetOption = {
    id?: string | number;
    name?: string;
};

type BrowseCategoryRawFacetCategories = {
    facets?: BrowseCategoryRawFacetCategoryNode[];
};

type BrowseCategoryRawFacetCategoryNode = BrowseCategoryRawFacetOption & {
    categories?: BrowseCategoryRawFacetCategories | BrowseCategoryRawFacetCategoryNode[];
};

/** Raw search facet row used when resolving SEO browse paths (facet id + flat options). */
export type BrowseCategoryRawFacet = {
    categories?: BrowseCategoryRawFacetCategories;
    id: string;
    options?: BrowseCategoryRawFacetOption[];
};

const getFacetCategoryChildren = (node: BrowseCategoryRawFacetCategoryNode): BrowseCategoryRawFacetCategoryNode[] => {
    if (!node.categories) {
        return [];
    }
    if (Array.isArray(node.categories)) {
        return node.categories;
    }
    return node.categories.facets ?? [];
};

const flattenFacetCategoryNodes = (nodes: BrowseCategoryRawFacetCategoryNode[]): BrowseCategoryRawFacetOption[] => {
    const out: BrowseCategoryRawFacetOption[] = [];
    const stack = [...nodes];
    while (stack.length > 0) {
        const current = stack.pop();
        if (!current) {
            continue;
        }
        out.push({ id: current.id, name: current.name });
        stack.push(...getFacetCategoryChildren(current));
    }
    return out;
};

/** Returns all candidate options for a facet from both `options` and `categories.facets` trees. */
export const getFacetOptionCandidates = (facet: BrowseCategoryRawFacet | undefined): BrowseCategoryRawFacetOption[] => {
    if (!facet) {
        return [];
    }
    const fromOptions = facet.options ?? [];
    const fromCategories = flattenFacetCategoryNodes(facet.categories?.facets ?? []);
    return [...fromOptions, ...fromCategories];
};

export const isKnownBrowseDimensionKey = (segment: string): boolean => {
    return Boolean(BROWSE_DIMENSION_PATH_TO_FACET_TYPE[segment]);
};

export const getFacetTypeFromBrowseDimensionKey = (dimensionKey: string): string | undefined =>
    BROWSE_DIMENSION_PATH_TO_FACET_TYPE[dimensionKey];

export const getBrowseDimensionKeyFromFacetType = (facetType: string): string | undefined =>
    BROWSE_FACET_TYPE_TO_DIMENSION_PATH_KEY[facetType];

const isSubjectLikeFacetId = (facetId: string): boolean => /subject/i.test(String(facetId));

const resolveFacetForBrowseDimension = (
    dimensionKey: string,
    rawFacets: BrowseCategoryRawFacet[]
): BrowseCategoryRawFacet | undefined => {
    const mappedFacetType = BROWSE_DIMENSION_PATH_TO_FACET_TYPE[dimensionKey];
    if (mappedFacetType) {
        const mappedFacet = rawFacets.find((facet) => facet.id === mappedFacetType);
        if (mappedFacet) {
            return mappedFacet;
        }
    }

    // Subject is the first data-driven browse dimension and can be surfaced under dynamic facet ids
    // (e.g. `subject`) instead of legacy `artSubject`.
    if (dimensionKey === 'subject') {
        const subjectFacet = rawFacets.find(
            (facet) => isSubjectLikeFacetId(facet.id) && getFacetOptionCandidates(facet).length > 0
        );
        if (subjectFacet) {
            return subjectFacet;
        }
    }

    return undefined;
};

/**
 * Parses the splat segment after `/c/{categorySlug}/` into dimension/value pairs.
 */
export const parseBrowseTail = (splat: string | undefined | null): BrowseDimensionPair[] => {
    if (!splat) {
        return [];
    }
    const segments = splat.split('/').filter(Boolean);
    const pairs: BrowseDimensionPair[] = [];
    for (let i = 0; i < segments.length; i += 2) {
        const dimensionKey = segments[i];
        const valueSlug = segments[i + 1];
        if (!dimensionKey || valueSlug === undefined) {
            break;
        }
        if (!isKnownBrowseDimensionKey(dimensionKey)) {
            break;
        }
        pairs.push({ dimensionKey, valueSlug });
    }
    return pairs;
};

export const sortBrowsePairsCanonical = (pairs: BrowseDimensionPair[]): BrowseDimensionPair[] => {
    return [...pairs].sort((a, b) => a.dimensionKey.localeCompare(b.dimensionKey));
};

const normalizeBrowsePathSlug = (slug: string): string => decodeURIComponent(slug).toLowerCase().trim();

/**
 * Creator L1 landing pages use `/c/{creatorSlug}/` as the base category. When a creator
 * dimension value slug matches that base, omit the redundant `/creator/{same-slug}/` segment.
 */
const pruneRedundantCreatorBrowsePairs = (
    categorySlug: string,
    pairs: BrowseDimensionPair[]
): BrowseDimensionPair[] => {
    const normalizedCategorySlug = normalizeBrowsePathSlug(categorySlug);
    if (!normalizedCategorySlug) {
        return pairs;
    }

    return pairs.flatMap((pair) => {
        if (pair.dimensionKey !== 'creator') {
            return [pair];
        }

        const valueSlugs = pair.valueSlug.split('~').filter(Boolean);
        const keptSlugs = valueSlugs.filter((slug) => normalizeBrowsePathSlug(slug) !== normalizedCategorySlug);
        if (keptSlugs.length === 0) {
            return [];
        }
        if (keptSlugs.length === valueSlugs.length) {
            return [pair];
        }
        return [{ ...pair, valueSlug: keptSlugs.join('~') }];
    });
};

export const buildBrowseCategoryPath = (
    categorySlug: string,
    pairs: BrowseDimensionPair[],
    options?: { base?: BrowsePathBase }
): string => {
    const base = options?.base ?? '/c';
    const sorted = sortBrowsePairsCanonical(pruneRedundantCreatorBrowsePairs(categorySlug, pairs));
    const tail = sorted.flatMap((p) => [p.dimensionKey, p.valueSlug]).join('/');
    return tail ? `${base}/${categorySlug}/${tail}/` : `${base}/${categorySlug}/`;
};

export const parseBrowseCategoryPath = (
    pathname: string
): {
    base: BrowsePathBase;
    categorySlug: string;
    pairs: BrowseDimensionPair[];
} | null => {
    const normalized = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const parts = normalized.split('/').filter(Boolean);
    if (parts.length < 2) {
        return null;
    }
    const first = parts[0];
    if (first !== 'c' && first !== 'price-guide' && first !== 'similar-items') {
        return null;
    }
    const base: BrowsePathBase =
        first === 'price-guide' ? '/price-guide' : first === 'similar-items' ? '/similar-items' : '/c';
    const categorySlug = parts[1];
    if (!categorySlug || isKnownBrowseDimensionKey(categorySlug)) {
        return null;
    }
    const rest = parts.slice(2);
    const pairs: BrowseDimensionPair[] = [];
    for (let i = 0; i < rest.length; i += 2) {
        const dimensionKey = rest[i];
        const valueSlug = rest[i + 1];
        if (!dimensionKey || valueSlug === undefined) {
            return null;
        }
        if (!isKnownBrowseDimensionKey(dimensionKey)) {
            return null;
        }
        pairs.push({ dimensionKey, valueSlug });
    }
    return { base, categorySlug, pairs };
};

/** Rebuilds a browse pathname, dropping redundant creator segments that match the category slug. */
export const sanitizeBrowseRedirectPath = (pathname: string): string => {
    const parsed = parseBrowseCategoryPath(pathname);
    if (!parsed) {
        return pathname;
    }
    return buildBrowseCategoryPath(parsed.categorySlug, parsed.pairs, { base: parsed.base });
};

export const getCanonicalBrowsePathForPathname = (pathname: string): string | null => {
    const parsed = parseBrowseCategoryPath(pathname);
    if (!parsed) {
        return null;
    }
    return buildBrowseCategoryPath(parsed.categorySlug, parsed.pairs, { base: parsed.base });
};

/**
 * Legacy `/c/{slug}/{numericId}/` (and price-guide / similar-items) before slug-only + dimension tails.
 */
export const parseLegacyCategoryPath = (
    pathname: string
): {
    base: BrowsePathBase;
    categorySlug: string;
    numericCategoryId: string;
} | null => {
    const normalized = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const parts = normalized.split('/').filter(Boolean);
    if (parts.length !== 3) {
        return null;
    }
    const [root, slug, maybeNum] = parts;
    if (!/^\d+$/.test(maybeNum)) {
        return null;
    }
    let base: BrowsePathBase;
    if (root === 'c') {
        base = '/c';
    } else if (root === 'price-guide') {
        base = '/price-guide';
    } else if (root === 'similar-items') {
        base = '/similar-items';
    } else {
        return null;
    }
    if (isKnownBrowseDimensionKey(slug)) {
        return null;
    }
    return { base, categorySlug: decodeURIComponent(slug), numericCategoryId: maybeNum };
};

export const browsePathHasTildeMultiValue = (pathname: string): boolean => {
    const parsed = parseBrowseCategoryPath(pathname);
    if (!parsed) {
        return false;
    }
    return parsed.pairs.some((p) => p.valueSlug.split('~').filter(Boolean).length > 1);
};

const optionSlugMatches = (option: BrowseCategoryRawFacetOption, slug: string): boolean => {
    const decodedSlug = decodeURIComponent(slug);
    if (String(option.id) === decodedSlug || String(option.id) === slug) {
        return true;
    }
    if (option.name && urlSafeTitle(option.name) === decodedSlug) {
        return true;
    }
    return false;
};

type BrowsePathFacetOption = {
    id?: string | number;
    name?: string;
    seoURL?: string;
};

export const resolveBrowseDimensionKeyForFacetId = (facetId: string): string | undefined => {
    const mapped = getBrowseDimensionKeyFromFacetType(facetId);
    if (mapped) {
        return mapped;
    }
    if (isSubjectLikeFacetId(facetId)) {
        return 'subject';
    }
    return undefined;
};

/**
 * When the URL is a browse dimension path, facet checked state is encoded in the pathname
 * (e.g. `/c/art/creator/andy-warhol/`), not query params. Returns `null` when the pathname
 * is not browse-shaped or the facet is not path-encoded, so callers can fall back to query
 * params / `option.selected`. Returns `false` when the browse path omits this dimension.
 */
export const isFacetOptionCheckedForBrowsePath = (
    pathname: string,
    facetId: string,
    option: BrowsePathFacetOption
): boolean | null => {
    const parsed = parseBrowseCategoryPath(pathname);
    if (!parsed) {
        return null;
    }

    const dimensionKey = resolveBrowseDimensionKeyForFacetId(facetId);
    if (!dimensionKey) {
        return null;
    }

    if (parsed.pairs.length === 0) {
        return null;
    }

    const pair = parsed.pairs.find((browsePair) => browsePair.dimensionKey === dimensionKey);
    if (!pair) {
        return false;
    }

    const slugs = pair.valueSlug.split('~').filter(Boolean);
    const normalizedOptionSlug = urlSafeTitle(option.name ?? '');
    const normalizedOptionSeoSlug = String(option.seoURL ?? '')
        .trim()
        .toLowerCase();

    return slugs.some((slug) => {
        const decodedSlug = decodeURIComponent(slug);
        return (
            optionSlugMatches(option, slug) ||
            slug === normalizedOptionSlug ||
            decodedSlug === normalizedOptionSlug ||
            decodedSlug.toLowerCase() === normalizedOptionSeoSlug
        );
    });
};

export const resolveFacetOptionValuesFromSemanticSlugs = (
    facetType: string,
    values: string[],
    rawFacets: BrowseCategoryRawFacet[]
): string[] => {
    const dimensionKey = getBrowseDimensionKeyFromFacetType(facetType);
    if (!dimensionKey) {
        return values;
    }
    const facet = resolveFacetForBrowseDimension(dimensionKey, rawFacets);
    if (!facet) {
        return values;
    }
    const candidates = getFacetOptionCandidates(facet);
    if (!candidates.length) {
        return values;
    }
    return values.map((value) => {
        const opt = candidates.find((option) => optionSlugMatches(option, value));
        if (opt?.id !== undefined && opt.id !== '') {
            return String(opt.id);
        }
        return value;
    });
};

type SemanticBrowseOptionKeys = {
    readKeys: string[];
    writeKey: string;
};

/**
 * `getFacetFromSearchQueryParameter` maps `subject=` → `options.artSubject`, but search-party often emits the
 * Subject facet under a data-driven id (e.g. `subject`). Read from legacy key, write resolved ids under the live id.
 */
const getSemanticBrowseReadWriteOptionKeys = (
    mappedFacetType: string,
    rawFacets: BrowseCategoryRawFacet[]
): SemanticBrowseOptionKeys => {
    if (mappedFacetType === FacetTypes.ART_SUBJECT_ID) {
        const facet = resolveFacetForBrowseDimension('subject', rawFacets);
        const writeKey = facet?.id ?? FacetTypes.ART_SUBJECT_ID;
        if (writeKey === FacetTypes.ART_SUBJECT_ID) {
            return { readKeys: [FacetTypes.ART_SUBJECT_ID], writeKey };
        }
        return { readKeys: [FacetTypes.ART_SUBJECT_ID, writeKey], writeKey };
    }
    return { readKeys: [mappedFacetType], writeKey: mappedFacetType };
};

/**
 * Keyword search URLs use semantic params (`creator=gucci`); search-party still expects facet option ids in
 * `options`. Map slugs (and passthrough numeric ids) using the latest facet aggregates when available.
 */
export const resolveSemanticBrowseDimensionValuesInSearchQuery = <
    T extends { categories?: string[]; options: Record<string, unknown> },
>(
    searchQuery: T,
    rawFacets: BrowseCategoryRawFacet[]
): T & { categories?: string[] } => {
    if (!rawFacets?.length) {
        return searchQuery;
    }
    const nextOptions: Record<string, unknown> = { ...searchQuery.options };
    let nextCategories: string[] | undefined = searchQuery.categories?.map(String);
    let optionsChanged = false;
    let categoriesChanged = false;

    for (const mappedFacetType of Object.values(BROWSE_DIMENSION_PATH_TO_FACET_TYPE)) {
        const { readKeys, writeKey } = getSemanticBrowseReadWriteOptionKeys(mappedFacetType, rawFacets);
        let raw: unknown;
        let readKeyUsed: string | undefined;
        for (const rk of readKeys) {
            const candidate = nextOptions[rk];
            if (Array.isArray(candidate) && candidate.length > 0) {
                raw = candidate;
                readKeyUsed = rk;
                break;
            }
        }
        if (!Array.isArray(raw) || raw.length === 0) {
            continue;
        }
        const strValues = raw.map((entry) => String(entry ?? '').trim());
        const resolved = resolveFacetOptionValuesFromSemanticSlugs(mappedFacetType, strValues, rawFacets);
        const resolvedDiffersFromInput = resolved.some((value, index) => value !== strValues[index]);
        const shouldMoveToCanonicalFacetKey = readKeyUsed !== undefined && readKeyUsed !== writeKey;
        if (resolvedDiffersFromInput || shouldMoveToCanonicalFacetKey) {
            if (readKeyUsed !== undefined && readKeyUsed !== writeKey) {
                delete nextOptions[readKeyUsed];
            }
            nextOptions[writeKey] = resolved;
            optionsChanged = true;
        }

        const allResolvedAreNumericTaxonomyIds =
            resolved.length > 0 &&
            resolved.every((value) => {
                const asString = String(value ?? '').trim();
                return asString.length > 0 && /^\d+$/.test(asString);
            });

        if (BROWSE_SEMANTIC_FACET_IDS_MERGED_INTO_CATEGORIES.has(mappedFacetType) && allResolvedAreNumericTaxonomyIds) {
            const base = nextCategories ?? [];
            const toAdd = resolved.map((id) => String(id)).filter((id) => !base.includes(id));
            if (toAdd.length > 0) {
                nextCategories = [...base, ...toAdd];
                categoriesChanged = true;
            }
        }
    }

    if (!optionsChanged && !categoriesChanged) {
        return searchQuery;
    }

    return {
        ...searchQuery,
        ...(optionsChanged ? { options: nextOptions } : {}),
        ...(categoriesChanged && nextCategories ? { categories: nextCategories } : {}),
    } as T & { categories?: string[] };
};

/**
 * Returns true when any semantic browse dimension in the search query's options still holds
 * a non-numeric slug (i.e. resolution against rawFacets has not yet occurred or failed).
 * Used to detect cold-load filter URLs where facets are not yet in Redux state.
 */
export const searchQueryHasUnresolvedSemanticBrowseValues = <T extends { options: Record<string, unknown> }>(
    searchQuery: T
): boolean =>
    Object.values(BROWSE_DIMENSION_PATH_TO_FACET_TYPE).some((facetType) => {
        const raw = searchQuery.options[facetType];
        return Array.isArray(raw) && raw.some((value) => !/^\d+$/.test(String(value ?? '').trim()));
    });

/**
 * Derives a fallback search keyword from any unresolved (non-numeric) semantic browse dimension
 * values in the search query's `options` (e.g. `material=oil` → "oil", `creator=frank-stella` →
 * "frank stella"). Normalizes dash-separated slugs to spaces, same as `browsePairsToFallbackKeyword`
 * in the category browse loader.
 *
 * Used in `searchPageData` to narrow the throwaway first request for keywordless cold loads
 * (e.g. `/search?material=oil`) so search-party actually surfaces the facet aggregation needed
 * to resolve the slug -- without SOME relevance narrowing, an unscoped search returns items across
 * every category and "Materials & Techniques" (and similar attribute facets) are never returned.
 */
export const getUnresolvedSemanticBrowseValuesAsKeyword = <T extends { options: Record<string, unknown> }>(
    searchQuery: T
): string =>
    Object.values(BROWSE_DIMENSION_PATH_TO_FACET_TYPE)
        .flatMap((facetType) => {
            const raw = searchQuery.options[facetType];
            if (!Array.isArray(raw)) {
                return [];
            }
            return raw
                .map((value) => String(value ?? '').trim())
                .filter((value) => value.length > 0 && !/^\d+$/.test(value));
        })
        .map((value) => value.replace(/-/g, ' ').trim())
        .filter(Boolean)
        .join(' ')
        .trim();

/**
 * Removes a facet option (by id string) from a browse URL path and returns the new pathname.
 */
export const removeBrowseFacetOptionFromPath = (
    pathname: string,
    optionId: string,
    rawFacets: BrowseCategoryRawFacet[]
): string | null => {
    const parsed = parseBrowseCategoryPath(pathname);
    if (!parsed) {
        return null;
    }
    const nextPairs: BrowseDimensionPair[] = [];
    for (const pair of parsed.pairs) {
        const facet = resolveFacetForBrowseDimension(pair.dimensionKey, rawFacets);
        const candidates = getFacetOptionCandidates(facet);
        const slugs = pair.valueSlug.split('~').filter(Boolean);
        const keptSlugs = slugs.filter((slug) => {
            const opt = candidates.find((o) => optionSlugMatches(o, slug));
            return !opt || String(opt.id) !== String(optionId);
        });
        if (keptSlugs.length > 0) {
            nextPairs.push({ dimensionKey: pair.dimensionKey, valueSlug: keptSlugs.join('~') });
        }
    }
    return buildBrowseCategoryPath(parsed.categorySlug, nextPairs, { base: parsed.base });
};

/**
 * Recovers the L1 category id for a top-cat slug (e.g. `watches`) by matching it against the F1
 * (category) facet returned in a recent search response. Use this when `resolve-browse-url` returned
 * nothing for `/c/{slug}/` and the slug isn't in the hardcoded top-cat alias map — the F1 facet
 * always contains the L1 the items belong to, so the keyword-narrowed initial search can backfill
 * the missing id for the categoryweb re-dispatch.
 *
 * Returns null when the facet, the slug, or a numeric id can't be found.
 */
export const resolveTopCategoryIdFromFacets = (
    categorySlug: string | undefined | null,
    rawFacets: BrowseCategoryRawFacet[]
): number | null => {
    if (!categorySlug) {
        return null;
    }
    const categoryFacet = rawFacets.find((facet) => facet.id === 'F1');
    if (!categoryFacet) {
        return null;
    }
    const candidates = getFacetOptionCandidates(categoryFacet);
    if (!candidates.length) {
        return null;
    }
    const matched = candidates.find((option) => optionSlugMatches(option, categorySlug));
    if (matched?.id === undefined || matched.id === '') {
        return null;
    }
    const numericId = Number(matched.id);
    return Number.isNaN(numericId) ? null : numericId;
};

/**
 * Resolves browse path pairs to search `options` facet id lists using raw search facets.
 */
export const mapBrowsePairsToSearchOptionIds = (
    pairs: BrowseDimensionPair[],
    rawFacets: BrowseCategoryRawFacet[]
): Record<string, string[]> => {
    const out: Record<string, string[]> = {};
    for (const { dimensionKey, valueSlug } of pairs) {
        const facet = resolveFacetForBrowseDimension(dimensionKey, rawFacets);
        if (!facet) {
            continue;
        }
        const candidates = getFacetOptionCandidates(facet);
        if (!candidates.length) {
            continue;
        }
        const slugs = valueSlug.split('~').filter(Boolean);
        const ids: string[] = [];
        for (const slug of slugs) {
            const opt = candidates.find((o) => optionSlugMatches(o, slug));
            if (opt?.id !== undefined && opt.id !== '') {
                ids.push(String(opt.id));
            }
        }
        if (ids.length) {
            out[facet.id] = ids;
        }
    }
    return out;
};
